import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { X, ShieldAlert, Volume2, VolumeX, BookOpen, Check, Wind, Bell } from 'lucide-react';
import { VoiceCoach, loadVoiceSettings } from '@/lib/voiceCoach';
import { BreathingRun } from '@/components/breathing/BreathingRun';
import { defaultMinutes, fmtClock, hasGuidedTimer, learnMoreUrl, patternText, SAFETY_TEXT } from '@/lib/breathing';
import { chimeEnabled, setChimeEnabled } from '@/lib/breathChime';
import { AMBIENCE, loadAmbience, saveAmbience } from '@/lib/breathAmbience';
import { AmbienceVolume } from '@/components/breathing/BreathingRun';

const HOLD_MODES = [{ id: 'full', label: 'Full holds' }, { id: 'short', label: 'Shorter holds' }, { id: 'skip', label: 'Skip holds' }];

const adjust = (phases, mode) => phases
  .filter(p => !(mode === 'skip' && p.type === 'hold'))
  .map(p => ({ ...p, seconds: mode === 'short' && p.type === 'hold' ? Math.max(1, Math.round(p.seconds / 2)) : +p.seconds }));

export default function BreathingSession() {
  const { id } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const [act, setAct] = useState(null);
  const [err, setErr] = useState(false);
  const [stage, setStage] = useState('setup');
  const [minutes, setMinutes] = useState(5);
  const [holdMode, setHoldMode] = useState('full');
  const [voiceOn, setVoiceOn] = useState(true);
  const [chimeOn, setChimeOn] = useState(chimeEnabled());
  const [amb, setAmb] = useState(loadAmbience());
  const changeAmb = (patch) => { const n = { ...amb, ...patch }; setAmb(n); saveAmbience(n.sound, n.volume); };
  const toggleChime = () => { setChimeEnabled(!chimeOn); setChimeOn(!chimeOn); };
  const [result, setResult] = useState(null);
  const voiceRef = useRef(null);

  useEffect(() => {
    api.get(`/recovery/activities/${id}`).then(r => { setAct(r.data); setMinutes(defaultMinutes(r.data)); setVoiceOn(r.data.breathing?.voice !== false && loadVoiceSettings().enabled !== false); })
      .catch(() => setErr(true));
  }, [id]);

  const phases = useMemo(() => (act ? adjust(act.breathing?.phases || [], holdMode) : []), [act, holdMode]);
  const cycleSec = phases.reduce((a, p) => a + p.seconds, 0);
  const totalCycles = cycleSec ? Math.max(1, Math.round((minutes * 60) / cycleSec)) : 0;

  useEffect(() => {
    if (!voiceOn || !act) { voiceRef.current?.dispose(); voiceRef.current = null; return; }
    const vc = new VoiceCoach({ ...loadVoiceSettings(), enabled: true });
    voiceRef.current = vc;
    vc.prefetch([...new Set((act.breathing?.phases || []).map(p => p.label || p.type))]);
    return () => vc.dispose();
  }, [voiceOn, act]);

  const back = () => (loc.key !== 'default' ? nav(-1) : nav('/app/recovery'));

  if (err) return <Shell onClose={back}><Empty text="This breathing exercise isn't available." /></Shell>;
  if (!act) return <Shell onClose={back}><div className="py-20 text-center text-muted-foreground text-sm">Loading…</div></Shell>;
  if (!hasGuidedTimer(act)) return <Shell onClose={back}><Empty text="The guided timer for this exercise isn't switched on yet." /></Shell>;

  return (
    <Shell onClose={back}>
      {stage === 'setup' && (
        <Setup act={act} minutes={minutes} setMinutes={setMinutes} holdMode={holdMode} setHoldMode={setHoldMode}
          voiceOn={voiceOn} setVoiceOn={setVoiceOn} chimeOn={chimeOn} toggleChime={toggleChime} amb={amb} changeAmb={changeAmb} cycleSec={cycleSec} totalCycles={totalCycles}
          onLearn={() => nav(learnMoreUrl(act), { state: { from: 'breathing' } })}
          onStart={() => setStage('run')} />
      )}
      {stage === 'run' && (
        <BreathingRun name={act.name} phases={phases} totalCycles={totalCycles} voice={voiceOn ? voiceRef.current : null} chime={chimeOn} ambience={amb}
          onFinish={(r) => { setResult({ ...r, sessionId: crypto.randomUUID() }); setStage('done'); }} />
      )}
      {stage === 'done' && result && <Done act={act} result={result} onClose={back} />}
    </Shell>
  );
}

const Shell = ({ children, onClose }) => (
  <div className="fixed inset-0 z-[200] bg-[var(--c-bg)] overflow-y-auto" data-testid="breathing-session">
    <div className="max-w-xl mx-auto px-5 pt-4 pb-10 min-h-full flex flex-col">
      <div className="flex justify-end"><button onClick={onClose} data-testid="breathing-close" aria-label="Close" className="p-2 rounded-full hover:bg-[var(--c-film)]"><X className="w-5 h-5" /></button></div>
      <div className="flex-1 flex flex-col justify-center">{children}</div>
    </div>
  </div>
);

const Empty = ({ text }) => <div className="py-20 text-center text-muted-foreground text-sm" data-testid="breathing-unavailable"><Wind className="w-7 h-7 mx-auto mb-2 opacity-50" />{text}</div>;

const Chip = ({ on, children, ...p }) => (
  <button {...p} className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${on ? 'bg-[#7DD3FC] text-[#081726]' : 'bg-[var(--c-film)] text-foreground/80'}`}>{children}</button>
);

function Setup({ act, minutes, setMinutes, holdMode, setHoldMode, voiceOn, setVoiceOn, chimeOn, toggleChime, amb, changeAmb, cycleSec, totalCycles, onLearn, onStart }) {
  const base = defaultMinutes(act);
  const options = [...new Set([2, 3, 5, 10, 15, base])].sort((a, b) => a - b);
  const hasHold = (act.breathing.phases || []).some(p => p.type === 'hold');
  return (
    <div className="space-y-5" data-testid="breathing-setup">
      <div>
        <div className="label-mini text-[var(--c-sky-ink)]">Guided breathing · optional</div>
        <h1 className="font-display font-black text-3xl mt-1" data-testid="breathing-setup-name">{act.name}</h1>
        <div className="text-sm text-muted-foreground mt-1 font-mono" data-testid="breathing-pattern">{patternText(act.breathing.phases)}</div>
      </div>
      <div className="rounded-2xl p-4 bg-[#FFB800]/10 border border-[#FFB800]/30 flex gap-3 text-sm" data-testid="breathing-safety">
        <ShieldAlert className="w-5 h-5 text-[#FFB800] flex-shrink-0 mt-0.5" /><p>{SAFETY_TEXT}</p>
      </div>
      <div>
        <div className="label-mini mb-2">Session length</div>
        <div className="flex flex-wrap gap-2">{options.map(m => <Chip key={m} on={minutes === m} onClick={() => setMinutes(m)} data-testid={`breathing-min-${m}`}>{m} min{m === base ? ' · default' : ''}</Chip>)}</div>
        <div className="text-xs text-muted-foreground mt-2" data-testid="breathing-plan">{totalCycles} cycles × {+cycleSec.toFixed(1)}s ≈ {fmtClock(totalCycles * cycleSec)}</div>
      </div>
      {hasHold && (
        <div>
          <div className="label-mini mb-2">Breath holds</div>
          <div className="flex flex-wrap gap-2">{HOLD_MODES.map(h => <Chip key={h.id} on={holdMode === h.id} onClick={() => setHoldMode(h.id)} data-testid={`breathing-holds-${h.id}`}>{h.label}</Chip>)}</div>
        </div>
      )}
      <div>
        <div className="label-mini mb-2">Background sound</div>
        <div className="flex flex-wrap gap-2">{AMBIENCE.map(s => <Chip key={s.id} on={amb.sound === s.id} onClick={() => changeAmb({ sound: s.id })} data-testid={`breathing-ambience-${s.id}`}>{s.label}</Chip>)}</div>
        {amb.sound !== 'off' && <div className="mt-3"><AmbienceVolume value={amb.volume} onChange={n => changeAmb({ volume: n })} label="Volume" /></div>}
      </div>
      <div className="flex flex-wrap gap-2">
        {act.breathing.voice !== false && (
          <Chip on={voiceOn} onClick={() => setVoiceOn(!voiceOn)} data-testid="breathing-voice-toggle">
            <span className="inline-flex items-center gap-1.5">{voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />} Voice {voiceOn ? 'on' : 'off'}</span>
          </Chip>
        )}
        <Chip on={chimeOn} onClick={toggleChime} data-testid="breathing-chime-toggle">
          <span className="inline-flex items-center gap-1.5"><Bell className="w-4 h-4" /> Chime {chimeOn ? 'on' : 'off'}</span>
        </Chip>
        {act.breathing.academy_article_id && <Chip onClick={onLearn} data-testid="breathing-learn-more"><span className="inline-flex items-center gap-1.5"><BookOpen className="w-4 h-4" /> Learn more</span></Chip>}
      </div>
      <button onClick={onStart} data-testid="breathing-start" className="w-full h-12 rounded-full bg-[#7DD3FC] text-[#081726] font-semibold">Start session</button>
    </div>
  );
}

function Done({ act, result, onClose }) {
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const canSave = result.cycles > 0;
  const save = async () => {
    setSaving(true);
    try {
      await api.post('/recovery/self-log', {
        activity_id: act.id, category: act.category, name: act.name, notes: 'Guided breathing',
        duration_min: Math.max(1, Math.round(result.seconds / 60)), duration_sec: result.seconds, cycles: result.cycles, session_id: result.sessionId,
      });
      setSaved(true); toast.success('Saved to your Recovery Log');
    } catch { toast.error('Could not save'); } finally { setSaving(false); }
  };
  return (
    <div className="text-center space-y-6" data-testid="breathing-complete">
      <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${canSave ? 'bg-[#65A30D]/15' : 'bg-[var(--c-film)]'}`}>{canSave ? <Check className="w-8 h-8 text-[#65A30D]" /> : <Wind className="w-8 h-8 text-muted-foreground" />}</div>
      <div>
        <div className="label-mini text-muted-foreground">{result.early ? 'Session ended' : 'Session complete'}</div>
        <h1 className="font-display font-black text-3xl mt-1" data-testid="breathing-complete-name">{act.name}</h1>
      </div>
      <div className="grid grid-cols-2 gap-3 max-w-xs mx-auto">
        <div className="regen-card-elevated p-3"><div className="label-mini text-muted-foreground">Duration</div><div className="font-display font-bold text-xl" data-testid="breathing-complete-duration">{fmtClock(result.seconds)}</div></div>
        <div className="regen-card-elevated p-3"><div className="label-mini text-muted-foreground">Cycles</div><div className="font-display font-bold text-xl" data-testid="breathing-complete-cycles">{result.cycles}</div></div>
      </div>
      {canSave ? (
        <button onClick={save} disabled={saved || saving} data-testid="breathing-save" className="w-full h-12 rounded-full bg-[#7DD3FC] text-[#081726] font-semibold disabled:opacity-60">
          {saved ? 'Saved to Recovery Log' : saving ? 'Saving…' : 'Save to Recovery Log'}
        </button>
      ) : <p className="text-sm text-muted-foreground" data-testid="breathing-nothing-to-save">Complete at least one full cycle to save a session.</p>}
      <p className="text-[11px] text-muted-foreground">Breathing sessions are for your personal history and don't affect your Recovery Score.</p>
      <button onClick={onClose} data-testid="breathing-done-close" className="text-sm text-muted-foreground underline underline-offset-4">Back</button>
    </div>
  );
}
